<script>
import { mdiClipboardTextOutline } from '@mdi/js'
import gql from 'graphql-tag'
import { toString } from '@/utils'
import DashboardSection from './dashboard_section.vue'

// Match the account quotes page's complete payload so either RMA flow can resume.
const SAVED_QUOTES_QUERY = gql`
    query DashboardSavedQuotes($first: Int, $sort: SortQuery) {
        savedQuoteConnection(service: "rma-manager", first: $first, sort: $sort) {
            totalCount
            pageInfo {
                hasPreviousPage
                hasNextPage
                startCursor
                endCursor
            }
            edges {
                cursor
                node {
                    uid
                    customerSaved
                    service
                    reference
                    externalResource
                    shipping {
                        address {
                            line1
                            line2
                            city
                            state
                            country
                            postalCode
                            international
                            apo
                        }
                        contact {
                            emailAddress
                            phoneNumber
                            firstName
                            lastName
                            companyName
                            fullName
                        }
                    }
                    billing {
                        address {
                            line1
                            line2
                            city
                            state
                            country
                            postalCode
                            international
                            apo
                        }
                        contact {
                            emailAddress
                            phoneNumber
                            firstName
                            lastName
                            companyName
                            fullName
                        }
                    }
                    items {
                        uid
                        name
                        price
                        quantity
                        tax
                        total
                    }
                    amount {
                        freight
                        handling
                        discount
                        tax
                    }
                    redirects {
                        completed
                        canceled
                    }
                    details {
                        payment {
                            uid
                            source
                            url
                            status
                            transaction
                            adminStatus
                            possibleStatusList
                        }
                        tax {
                            uid
                            source
                            status
                        }
                    }
                    createdAt
                    updatedAt
                    customerPo {
                        poNumber
                        poFileId
                    }
                    createdBy
                    subtotal
                    total
                }
            }
        }
    }
`

export default {
    name: 'SavedQuotesSection',
    components: { DashboardSection },
    data() {
        return {
            quotes: [],
            totalCount: 0,
            loading: true,
            fetchingQuotes: false,
            error: '',
            detailsError: ''
        }
    },
    computed: {
        section() {
            return {
                id: 'quotes',
                title: 'Saved quotes',
                icon: mdiClipboardTextOutline,
                description: 'Pick up where you left off with your service estimates.',
                link: '/your-account/saved-quotes',
                action: 'View quotes',
                rows: this.quotes.map((quote) => ({
                    id: String(quote.uid),
                    title: `Quote #${quote.uid}`,
                    detail: [
                        `Serial ${quote.externalResource?.serial || 'Unavailable'}`,
                        quote.externalResource?.rmaInput?.primaryFault,
                        quote.externalResource?.rmaInput?.problem || 'Issue unavailable'
                    ].filter(Boolean).join(' · '),
                    // The shared badge displays the amount, not an invented quote status.
                    status: this.formatTotal(quote.total),
                    tone: 'neutral'
                })),
                footnote: this.loading
                    ? 'Loading your saved quotes…'
                    : this.error
                    ? 'Saved quotes are temporarily unavailable'
                    : `Showing ${this.quotes.length} of ${this.totalCount} quotes · Most recent first`
            }
        }
    },
    mounted() {
        this.fetchQuotes()
    },
    methods: {
        formatTotal(value) {
            if (value == null || value === '' || !Number.isFinite(Number(value))) return 'Total unavailable'
            return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Number(value))
        },
        emitSummary() {
            this.$emit('summary', {
                metric: this.loading ? '…' : this.error ? '—' : String(this.totalCount).padStart(2, '0'),
                metricLabel: 'Total saved quotes'
            })
        },
        async fetchQuotes() {
            if (this.fetchingQuotes) return
            this.fetchingQuotes = true
            this.loading = true
            this.error = ''
            this.detailsError = ''
            this.emitSummary()
            try {
                // The resolver scopes saved quotes to the authenticated user; no client user filter needed.
                // A first-page request replaces existing relay edges, as with parts orders.
                const { data } = await this.$apollo.query({
                    query: SAVED_QUOTES_QUERY,
                    variables: { first: 3, sort: { key: 'createdAt', descending: true } },
                    fetchPolicy: 'network-only'
                })
                const connection = data?.savedQuoteConnection
                if (!connection || !Array.isArray(connection.edges) ||
                    !Number.isInteger(connection.totalCount) || connection.totalCount < 0) {
                    throw new Error('Saved quotes unavailable')
                }
                const timestamp = (value) => Date.parse(value) || 0
                this.quotes = connection.edges.map((edge) => edge?.node).filter((quote) => quote?.uid)
                    .sort((a, b) => timestamp(b.createdAt) - timestamp(a.createdAt)).slice(0, 3)
                this.totalCount = connection.totalCount
            } catch (error) {
                this.quotes = []
                this.totalCount = 0
                this.error = 'Your saved quotes could not be loaded. Please try again.'
            } finally {
                this.fetchingQuotes = false
                this.loading = false
                this.emitSummary()
            }
        },
        continueRma(uid) {
            this.detailsError = ''
            const quote = this.quotes.find((item) => String(item.uid) === uid)
            const resource = quote?.externalResource
            if (!resource || typeof resource !== 'object' || Array.isArray(resource) ||
                typeof resource.serial !== 'string' || !resource.serial.trim() ||
                !resource.rmaInput || typeof resource.rmaInput !== 'object' || Array.isArray(resource.rmaInput) ||
                typeof resource.rmaInput.primaryFault !== 'string' || !resource.rmaInput.primaryFault.trim()) {
                this.detailsError = 'We could not resume this quote. Please view your saved quotes or contact support.'
                return
            }
            const upgrade = resource.rmaInput.primaryFault === 'UPGRADE'
            this.$router.push({
                name: upgrade ? 'upgrade-device' : 'create-rma',
                params: { serial: resource.serial, toPage: upgrade ? 5 : 4, quoteData: quote },
                query: { quoteId: quote.uid, prefill: toString(resource) }
            })
        }
    }
}
</script>

<template>
    <dashboard-section
        :section="section"
        :loading="loading"
        :error="error"
        :details-error="detailsError"
        loading-text="Loading your saved quotes…"
        empty-text="No saved quotes yet. Your saved service estimates will appear here."
        selectable
        @retry="fetchQuotes"
        @select="continueRma"
    />
</template>
